import { Flex, Text } from "@radix-ui/themes";
import { ManifestLoadedEvent } from "dashjs";
import { PlanogramViewer } from "@/features/planogram-viewer";
import {
  canStartVideo,
  updateCurrentTime,
  updateLiveEdge,
  updateVideoStream,
} from "@/entities/player";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { ManifestLoadedData } from "@/shared/types";
import { MediaPlayerProvider } from "@/shared/ui";
import { Controls } from "./controls";
import { Header } from "./header";
import { Timeline } from "./timeline";
import { VideoPlayer, VideoPlayerContainer } from "./video-player";
import { calculateComputedAvailabilityEndTime } from "@/shared/helpers/calculate-computed-availability-end-time";

export function Planogram() {
  const videoParams = useAppSelector((state) => state.player.videoStream);
  const timeshift = useAppSelector(
    (state) => state.player.preferences.timeshift
  );
  const dispatch = useAppDispatch();
  const isPlayable = useAppSelector(canStartVideo);

  const onManifestLoaded = (manifest: ManifestLoadedEvent) => {
    const { Period, availabilityStartTime } =
      manifest.data as ManifestLoadedData;

    const availabilityEndTime = calculateComputedAvailabilityEndTime(
      Period,
      availabilityStartTime
    );

    dispatch(
      updateVideoStream({
        availabilityStartTime: availabilityStartTime.getTime(),
        computedAvailabilityEndTime: availabilityEndTime?.getTime() || null,
      })
    );
  };

  const onTimeChange = (time: number | null, liveEdge: number | null) => {
    dispatch(updateCurrentTime(time));
    dispatch(updateLiveEdge(liveEdge));
  };

  return (
    <MediaPlayerProvider>
      <Flex direction="column" className="h-dvh">
        <Header />

        <PlanogramViewer
          renderVideo={
            <VideoPlayerContainer>
              {isPlayable ? (
                <VideoPlayer
                  timeshift={timeshift}
                  params={videoParams}
                  onTimeChange={onTimeChange}
                  onManifestLoaded={onManifestLoaded}
                />
              ) : (
                <VideoNotInitialized />
              )}
            </VideoPlayerContainer>
          }
        />

        <Flex
          direction="column"
          className="w-full border-t border-gray-200 dark:border-[var(--gray-a7)] grow"
        >
          <Controls
            videoParams={videoParams}
            setVideoParams={(videoParams) => {
              dispatch(updateVideoStream(videoParams));
            }}
          />
          <Timeline />
        </Flex>
      </Flex>
    </MediaPlayerProvider>
  );
}

function VideoNotInitialized() {
  const cameraId = useAppSelector((state) => state.player.videoStream.cameraId);

  return (
    <div className="w-full h-full flex flex-col justify-center items-center">
      <Text size="5" weight="bold" mb="4">
        Video is not initialized
      </Text>
      {!cameraId ? (
        <Text size="2">Please select a camera from bottom left corner</Text>
      ) : (
        <Text size="2">
          Press "Go live" or select a time range and press "Apply"
        </Text>
      )}
    </div>
  );
}
